import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { authFetch } from '../../utils/authFetch';

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: {
    type: string;
    text?: string;
    example?: {
      body_text?: string[][];
    };
  }[];
}

interface TemplateComposerProps {
  template: Template;
  chatId: string;
  contact?: any;
  onClose: () => void;
  onSent: () => void;
}

const TemplateComposer: React.FC<TemplateComposerProps> = ({ 
  template, 
  chatId,
  contact: contactProp,
  onClose, 
  onSent 
}) => {
  const [variables, setVariables] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [contact, setContact] = useState<any>(contactProp || null);

  useEffect(() => {
    // Load contact data for this chat if not provided
    if (!contactProp) {
      loadContact();
    }
    
    // Extract variable count from template body
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || '';
    const matches = bodyText.match(/\{\{\d+\}\}/g);
    const varCount = matches ? matches.length : 0;
    
    // Initialize with example values if available
    const examples = bodyComponent?.example?.body_text?.[0] || [];
    setVariables(Array(varCount).fill('').map((_, i) => examples[i] || ''));
  }, [template, chatId, contactProp]);

  const loadContact = async () => {
    try {
      // authFetch automatically prepends /api
      const response = await authFetch(`/chat/${chatId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.contact) {
          setContact(data.data.contact);
        }
      }
    } catch (error) {
      // Silent fail - contact data is optional
    }
  };

  const getTemplateBody = (): string => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    return bodyComponent?.text || '';
  };

  const getPreview = (): string => {
    let preview = getTemplateBody();
    variables.forEach((value, index) => {
      preview = preview.replace(`{{${index + 1}}}`, value || `{{${index + 1}}}`);
    });
    return preview;
  };

  const handleVariableChange = (index: number, value: string) => {
    const newVariables = [...variables];
    newVariables[index] = value;
    setVariables(newVariables);
  };

  const canSend = (): boolean => {
    return variables.every(v => v.trim() !== '');
  };

  const handleSend = async () => {
    if (!canSend()) {
      alert('Please fill in all variables');
      return;
    }

    setSending(true);
    try {
      // axios already has /api as baseURL
      await axios.post('/templates/send', {
        chatId,
        templateName: template.name,
        languageCode: template.language,
        variables,
        templateBody: getPreview() // Send the actual message content
      });
      
      onSent();
      onClose();
    } catch (error) {
      console.error('Error sending template:', error);
      alert('Failed to send template message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        {/* Header */}
        <div className="assignment-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px'
              }}
            >
              ← Back
            </button>
            <span style={{ color: '#d1d5db' }}>|</span>
            <h4>{template.name}</h4>
          </div>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        {/* Content */}
        <div className="assignment-content" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {/* Template Info */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <span style={{ 
                fontSize: '11px', 
                padding: '4px 8px', 
                backgroundColor: '#dbeafe', 
                color: '#1e40af', 
                borderRadius: '4px',
                fontWeight: '500'
              }}>
                {template.category}
              </span>
              <span style={{ 
                fontSize: '11px', 
                padding: '4px 8px', 
                backgroundColor: '#f3f4f6', 
                color: '#374151', 
                borderRadius: '4px',
                fontWeight: '500'
              }}>
                {template.language}
              </span>
            </div>
          </div>

          {/* Variables Input */}
          {variables.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
                Variables
              </h5>
              {variables.map((value, index) => (
                <div key={index} className="form-group" style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                      Variable {index + 1}
                    </label>
                    {contact && (
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleVariableChange(index, e.target.value);
                          }
                        }}
                        style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Insert field...</option>
                        {contact.firstName && <option value={contact.firstName}>First Name</option>}
                        {contact.lastName && <option value={contact.lastName}>Last Name</option>}
                        {contact.name && <option value={contact.name}>Full Name</option>}
                        <option value={contact.phoneNumber}>Phone Number</option>
                      </select>
                    )}
                  </div>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleVariableChange(index, e.target.value)}
                    placeholder={`Enter value ${index + 1}`}
                    className="form-input"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Preview */}
          <div>
            <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
              Preview
            </h5>
            <div style={{ 
              backgroundColor: '#dcfce7', 
              border: '1px solid #86efac', 
              borderRadius: '8px', 
              padding: '12px' 
            }}>
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                padding: '12px', 
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' 
              }}>
                <p style={{ fontSize: '13px', whiteSpace: 'pre-wrap', margin: 0, lineHeight: '1.5' }}>
                  {getPreview()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="assignment-actions">
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend() || sending}
            className="assign-button"
            style={{
              opacity: canSend() && !sending ? 1 : 0.5,
              cursor: canSend() && !sending ? 'pointer' : 'not-allowed'
            }}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateComposer;

